import { lang } from '@umu/ukit-lang';
import React, { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { components } from 'common';
const { TimePicker } = components;
import { IconTips } from '../@iconTips/iconTips';
import './assignRangePicker.scss';

// 学习任务1期项目时间选择规则优化
// 1. 分配时间：当前时间之前的时间不能选择
// 2. 到期时间：分配时间+30分钟之前的时间不能选择
// 3. 当【既有分配时间，又有到期时间】时，改变分配时间，持续时间段不变，到期时间联动
// 4. 当【既有分配时间，又有到期时间】时，改变到期时间，持续时间段改变，分配时间不变
// 5. 如果有timeSpanEnd，则

export interface IParams {
    assignTime: string;
    expireTime: string;
}
export interface IProps {
    handlerConfirmSelect: (params: IParams) => void;
    assignTime: string;
    expireTime?: string;
    format?: string;
    timeSpanMin?: number; // 时间最小间隔，单位：分
    timeSpanMax?: number; // 时间最大间隔，单位：分
    isShowDurationLabel?: boolean;
    assignTimePlaceholder?: string;
    expireTimePlaceholder?: string;
}

const FORMAT_TIME_RULE = 'YYYY-MM-DD HH:mm';
const TIME_SPAN_MIN = 30; // 分配时间+30分钟之前的时间不能选择
const TIME_SPAN_MAX = undefined;    // 分配时间之后的时间不能选择

const LABEL = lang(
    '如果不设置到期时间，则任务通知高级设置中勾选的到期前、到期日和过期后提醒将不会对被分配任务的学员生效。',
);

const checkIsSpanDay = (startDay: dayjs.Dayjs, endDay: dayjs.Dayjs) => {
    const spanDays = dayjs(endDay.format('YYYYMMDD')).diff(dayjs(startDay.format('YYYYMMDD')), 'day');
    return spanDays >= 1;
}

export function AssignRangePicker(props: IProps) {
    const {
        timeSpanMin = TIME_SPAN_MIN,
        timeSpanMax = TIME_SPAN_MAX,
        isShowDurationLabel = true,
        assignTimePlaceholder = '',
        expireTimePlaceholder = ''
    } = props;

    // 分配时间
    const [assignTime, setAssignTime] = useState(dayjs(props.assignTime).format(FORMAT_TIME_RULE));
    const [expireTime, setExpireTime] = useState(!!props.expireTime ? dayjs(props.expireTime).format(FORMAT_TIME_RULE) : '');

    // 持续时间
    const [durationMinutes, setDurationMinutes] = useState(!!props.expireTime ? dayjs(props.expireTime).diff(props.assignTime, 'minute'): 0);

    // 不可选中时间辅助方法
    function range(start: number, end: number) {
        return Array.from({ length: end - start }, (_, i) => start + i);
    }

    const disabledAssignDate = (date: any) => {
        return date && date < dayjs().startOf('day');
    };

    const disabledAssignTime = () => {
        const now = dayjs();
        // 分配时间默认为当前时间，当前选中的分配时间于当前时刻相比，已经跨天，小时和分钟数都可选
        const startDay = now;
        let endDay = dayjs(assignTime);

        const isSpanDay = checkIsSpanDay(startDay, endDay);

        if (isSpanDay) {
            return {
                disabledHours: () => [],
                disabledMinutes: () => [],
            };
        }

        // 分配时间默认为当前时间，当前选中的分配时间于当前时刻相比，已经跨小时，当前小时之前的数不可选，所有分钟都可选
        const isSpanMinute = endDay.diff(startDay, 'minute') > 0;
        if (isSpanMinute) {
            return {
                disabledHours: () => range(0, 24).splice(0, now.hour()),
                disabledMinutes: () => [],
            };
        }
        return {
            disabledHours: () => range(0, 24).splice(0, now.hour()),
            disabledMinutes: () => range(0, 60).splice(0, now.minute()),
        };
    };

    const disabledExpireTime = useCallback((date: any) => {
        if (!date) {
            return;
        }
        // 到期时间为默认时间之后的天数，所有小时和所有分钟都可选
        const startDay = dayjs(expireTime);
        const endDateMin = dayjs(assignTime).add(timeSpanMin, 'minute');
        const isEndDateMinSpanDay = checkIsSpanDay(endDateMin, startDay);
        const isEndDateMinSpanHour = startDay.diff(endDateMin, 'hour') >= 1;

        if (timeSpanMax) {
            const endDateMax = dayjs(assignTime).add(timeSpanMax, 'minute');
            const isDateDayEndDateMaxDaySame = date.isSame(endDateMax, 'day');

            if (!isDateDayEndDateMaxDaySame) {
                if (isEndDateMinSpanDay) {
                    return {
                        disabledHours: () => [],
                        disabledMinutes: () => [],
                    }
                }

                if (isEndDateMinSpanHour) {
                    return {
                        disabledHours: () => range(0, 24).splice(0, dayjs(assignTime).hour()),
                        disabledMinutes: () => [],
                    };
                }
            } else {
                const isEndDateMaxSpanHour = !endDateMax.isSame(startDay, 'hour');

                if (isEndDateMaxSpanHour) {
                    return {
                        disabledHours: () => range(0, 24).splice(dayjs(assignTime).hour() + 1, 24),
                        disabledMinutes: () => [],
                    };
                }

                return {
                    disabledHours: () => range(0, 24).splice(dayjs(assignTime).hour() + 1, 24),
                    disabledMinutes: () => range(0, 60).splice(dayjs(assignTime).minute() + 1, 60),
                };
            }

            // 到期时间默认为分配时间timeSpan min，默认时间的之前的小时和分钟数字不可选
            return {
                disabledHours: () => range(0, 24).splice(0, dayjs(assignTime).add(timeSpanMin, 'minute').hour()),
                disabledMinutes: () => range(0, 60).splice(0, dayjs(assignTime).add(timeSpanMin, 'minute').minute()),
            };
        }

        if (isEndDateMinSpanDay) {
            return {
                disabledHours: () => [],
                disabledMinutes: () => [],
            };
        }

        // 到期时间为默认时间之后的小时数，默认小时及其之前不可选，所有分钟可选
        if (isEndDateMinSpanHour) {
            return {
                disabledHours: () => range(0, 24).splice(0, dayjs(assignTime).add(timeSpanMin, 'minute').hour()),
                disabledMinutes: () => [],
            };
        }

        // 到期时间默认为分配时间timeSpan min，默认时间的之前的小时和分钟数字不可选
        return {
            disabledHours: () => range(0, 24).splice(0, dayjs(assignTime).add(timeSpanMin, 'minute').hour()),
            disabledMinutes: () => range(0, 60).splice(0, dayjs(assignTime).add(timeSpanMin, 'minute').minute()),
        };
    }, [assignTime, expireTime]);

    // 到期时间
    const disabledExpireDate = useCallback(
        (date: any) => {
            if (!date) {
                return false;
            }

            // 分配时间+timeSpan min跨天，当天及其之前时间不可选
            const startDate = dayjs(assignTime);
            const endDateMin = dayjs(assignTime).add(timeSpanMin, 'minute');
            // 开始时间和最短设置时间有一天以上的间隔
            const isEndDateMinSpanDay = checkIsSpanDay(startDate, endDateMin);

            if (isEndDateMinSpanDay) {
                // 设置不可选中日期
                return date < dayjs(assignTime).endOf('day');
            }

            // 设置了最大可选时间
            if (timeSpanMax) {
                const endDateMax = dayjs(assignTime).add(timeSpanMax, 'minute');

                return date > dayjs(endDateMax).endOf('day') || date < dayjs(assignTime).startOf('day');
            }

            //  分配时间+timeSpan min为当天，当天之前的时间不可选
            return date < dayjs(assignTime).startOf('day');
        },
        [assignTime, expireTime],
    );

    // 修改持续时间文案
    const returnDurationLabel = useCallback(() => {
        if (durationMinutes === 0 || !isShowDurationLabel) {
            return ""
        }
        if (durationMinutes < timeSpanMin) {
            return lang('最小间隔时间为30分钟').replace('30', timeSpanMin);
        }

        if (timeSpanMax && durationMinutes > timeSpanMax) {
            return lang('最大间隔时间为30分钟').replace('30', timeSpanMax);
        }

        const showMinutes = durationMinutes % 60;
        let showHours = durationMinutes / 60;
        if (showHours < 1) {
            return lang('共计{$0}分钟', showMinutes);
        }
        const showDays = showHours / 24;
        if (showDays < 1) {
            return lang('共计{$0}小时{$1}分钟', Math.floor(showHours), showMinutes);
        }
        showHours = showHours % 24;
        return lang('共计{$0}天{$1}小时{$2}分钟', Math.floor(showDays), Math.floor(showHours), showMinutes);
    }, [assignTime, expireTime, durationMinutes, isShowDurationLabel, timeSpanMin, timeSpanMax]);

    // 分配时间选择发生变化
    const assignTimeSelectedChange = useCallback(
        (value: string) => {
            const now = dayjs();
            // 判断当前选中时间是否在默认分配时间（当前时刻）之前，如果是，当前选中时间设置为当前时刻
            if (dayjs(value).diff(now, 'minute', true) < 0) {
                setAssignTime(now.format(FORMAT_TIME_RULE));
                // 判断是否有持续时间，有持续时间，到期时间为当前时刻+持续时间
                if (durationMinutes > 0) {
                    setExpireTime(now.add(durationMinutes, 'minute').format(FORMAT_TIME_RULE));
                    props.handlerConfirmSelect({
                        assignTime: now.format(FORMAT_TIME_RULE),
                        expireTime: now.add(durationMinutes, 'minute').format(FORMAT_TIME_RULE),
                    });
                } else {
                    // 没有持续时间，到期时间为默认值('')
                    props.handlerConfirmSelect({
                        assignTime: now.format(FORMAT_TIME_RULE),
                        expireTime: '',
                    });
                }
                return;
            }
            setAssignTime(dayjs(value).format(FORMAT_TIME_RULE));
            if (durationMinutes > 0) {
                setExpireTime(dayjs(value).add(durationMinutes, 'minute').format(FORMAT_TIME_RULE));

                props.handlerConfirmSelect({
                    assignTime: dayjs(value).format(FORMAT_TIME_RULE),
                    expireTime: dayjs(value).add(durationMinutes, 'minute').format(FORMAT_TIME_RULE),
                });
                return;
            }
            // 没有持续时间，到期时间为默认值('')
            props.handlerConfirmSelect({
                assignTime: dayjs(value).format(FORMAT_TIME_RULE),
                expireTime: '',
            });
        },
        [assignTime, expireTime],
    );

    // 到期时间选择发生变化
    const expireTimeSelectedChange = useCallback(
        (value: string) => {
            // 当选择的到期时间与分配时间的时间间隔小于timeSpan min，设置到期时间为分配时间+timeSpan min
            if (dayjs(value).diff(assignTime, 'minute', true) < timeSpanMin) {
                setExpireTime(dayjs(assignTime).add(timeSpanMin, 'minute').format(FORMAT_TIME_RULE));
                setDurationMinutes(timeSpanMin);
                props.handlerConfirmSelect({
                    assignTime: dayjs(assignTime).format(FORMAT_TIME_RULE),
                    expireTime: dayjs(assignTime).add(timeSpanMin, 'minute').format(FORMAT_TIME_RULE),
                });
                return;
            }
            if (timeSpanMax && dayjs(value).diff(assignTime, 'minute', true) > timeSpanMax) {
                setExpireTime(dayjs(assignTime).add(timeSpanMax, 'minute').format(FORMAT_TIME_RULE));
                setDurationMinutes(timeSpanMax);
                props.handlerConfirmSelect({
                    assignTime: dayjs(assignTime).format(FORMAT_TIME_RULE),
                    expireTime: dayjs(assignTime).add(timeSpanMax, 'minute').format(FORMAT_TIME_RULE),
                });
                return;
            }
            setExpireTime(dayjs(value).format(FORMAT_TIME_RULE));
            setDurationMinutes(dayjs(value).diff(assignTime, 'minute'));
            props.handlerConfirmSelect({
                assignTime: dayjs(assignTime).format(FORMAT_TIME_RULE),
                expireTime: dayjs(value).format(FORMAT_TIME_RULE),
            });
        },
        [assignTime, expireTime],
    );

    // 删除到期时间的处理
    const onExpireTimeChange = useCallback(
        (value: string) => {
            if (value === '') {
                if (timeSpanMax) {
                    setExpireTime(dayjs(assignTime).add(timeSpanMax, 'minute').format(FORMAT_TIME_RULE));
                    setDurationMinutes(timeSpanMax);
                    props.handlerConfirmSelect({
                        assignTime: dayjs(assignTime).format(FORMAT_TIME_RULE),
                        expireTime: dayjs(assignTime).add(timeSpanMax, 'minute').format(FORMAT_TIME_RULE),
                    });
                    return;
                }

                props.handlerConfirmSelect({
                    assignTime: dayjs(assignTime).format(FORMAT_TIME_RULE),
                    expireTime: '',
                });
                setDurationMinutes(0);
                setAssignTime(dayjs(assignTime).format(FORMAT_TIME_RULE));
                setExpireTime('');
                return;
            }
        },
        [assignTime, expireTime, durationMinutes],
    );

    return (
        <div className="assign-range-time pc-common-assign-range-time">
            <div className="assign-time-content">
                <div className="assign-time-title">{lang('分配时间_Assignment_time')}</div>
                <div className="assign-time">
                    {TimePicker({
                        placeholder: assignTimePlaceholder,
                        format: props.format || FORMAT_TIME_RULE,
                        handlerConfirmSelect: assignTimeSelectedChange,
                        initValue: assignTime,
                        readonly: true,
                        disabledDate: disabledAssignDate,
                        disabledTime: disabledAssignTime,
                        allowClear: false,
                        showNow: true,
                        onChange: (value: string) => {
                            console.log(value);
                        },
                    })}
                </div>
            </div>
            <div className="expire-time-content">
                <div className="expire-time-title">
                    <span className="expire-time-title-label">{lang('到期时间')}</span>
                    {IconTips(LABEL)}
                </div>
                <div className="expire-time">
                    {TimePicker({
                        placeholder: expireTimePlaceholder,
                        format: props.format || FORMAT_TIME_RULE,
                        handlerConfirmSelect: expireTimeSelectedChange,
                        initValue: expireTime,
                        readonly: false,
                        disabledDate: disabledExpireDate,
                        disabledTime: disabledExpireTime,
                        allowClear: true,
                        showNow: false,
                        onChange: onExpireTimeChange,
                    })}
                </div>
            </div>
            <div className="duration-label">{returnDurationLabel()}</div>
        </div>
    );
}
